import {useEffect, useState} from 'react'
import {Card} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {UPDATE_AGENT_ONBOARDING_INTERNAL_NOTES} from '../../../gql/mutations/agentOnboardingMutations'

const M = defineMessages({
  title: {id: 'onboarding.notes.title', defaultMessage: 'Internal notes'},
  subtitle: {
    id: 'onboarding.notes.subtitle',
    defaultMessage: 'Visible to the contracting team only. The applicant never sees these.',
  },
  placeholder: {
    id: 'onboarding.notes.placeholder',
    defaultMessage: 'Add notes about this applicant…',
  },
  save: {id: 'onboarding.notes.save', defaultMessage: 'Save notes'},
  saved: {id: 'onboarding.notes.saved', defaultMessage: 'Notes saved'},
  failed: {id: 'onboarding.notes.failed', defaultMessage: 'Could not save the notes'},
})

interface Props {
  applicationId: string
  notes: string
  canEdit: boolean
}

export function InternalNotes({applicationId, notes, canEdit}: Props) {
  const intl = useIntl()
  const [value, setValue] = useState(notes)
  const [saveNotes, {loading}] = useMutation(UPDATE_AGENT_ONBOARDING_INTERNAL_NOTES)

  // The application arrives asynchronously; adopt the stored notes once, without stomping on
  // whatever the reviewer may already be typing.
  useEffect(() => {
    setValue(notes)
  }, [notes])

  const handleSave = async () => {
    try {
      const res = await saveNotes({variables: {applicationId, internalNotes: value}})
      const payload = res.data?.updateAgentOnboardingInternalNotes
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message ?? intl.formatMessage(M.failed))
        return
      }
      toast.success(intl.formatMessage(M.saved))
    } catch {
      toast.error(intl.formatMessage(M.failed))
    }
  }

  return (
    <Card className='h-100'>
      <Card.Header className='border-0 pt-6'>
        <div>
          <h3 className='fw-bold text-gray-900 mb-1'>{intl.formatMessage(M.title)}</h3>
          <span className='text-gray-600 fs-7'>{intl.formatMessage(M.subtitle)}</span>
        </div>
      </Card.Header>
      <Card.Body className='pt-2'>
        <textarea
          className='form-control form-control-solid mb-4'
          rows={6}
          value={value}
          disabled={!canEdit}
          placeholder={intl.formatMessage(M.placeholder)}
          onChange={(e) => setValue(e.target.value)}
        />
        {canEdit && (
          <button
            type='button'
            className='btn btn-light-primary'
            disabled={loading || value === notes}
            onClick={handleSave}
          >
            <i className='bi bi-save me-1' />
            {intl.formatMessage(M.save)}
          </button>
        )}
      </Card.Body>
    </Card>
  )
}
